import { fingering } from "../lib/fingering";

/**
 * A chord box, drawn the way a songbook draws one: six strings, four frets,
 * open and muted strings marked above the nut.
 *
 * The label handed in is already transposed and capoed, so the shape shown is
 * the shape the player actually makes — with a capo on, the diagram is relative
 * to the capo, which is how a person thinks about it.
 */
export function ChordDiagram({ label, size = 54 }: { label: string; size?: number }) {
  const shape = fingering(label);
  if (!shape) return null;

  const strings = 6;
  const frets = 4;
  const padX = size * 0.16;
  const padTop = size * 0.30;
  const width = size;
  const height = size * 1.28;
  const gridW = width - padX * 2;
  const gridH = height - padTop - size * 0.13;
  const stringGap = gridW / (strings - 1);
  const fretGap = gridH / frets;

  const open = shape.baseFret === 1;
  // A shape sitting high on the neck is drawn from its own first fret.
  const from = open ? 1 : shape.baseFret;
  const at = (fret: number) => fret - from + 1;

  const dots = shape.frets
    .map((fret, index) => ({ fret, index }))
    .filter((d) => d.fret > 0 && at(d.fret) >= 1 && at(d.fret) <= frets);

  const barreFret = shape.barre && at(shape.barre) === 1 ? shape.barre : null;
  const barreFrom = barreFret
    ? shape.frets.findIndex((f) => f === barreFret)
    : -1;

  return (
    <svg
      width={width} height={height} viewBox={`0 0 ${width} ${height}`}
      className="diagram" role="img" aria-label={`${label} chord shape`}
    >
      {/* open and muted strings, above the nut */}
      {shape.frets.map((fret, index) => {
        const x = padX + index * stringGap;
        if (fret > 0) return null;
        return fret === 0 ? (
          <circle key={index} cx={x} cy={padTop - size * 0.14} r={size * 0.055}
                  fill="none" stroke="currentColor" strokeWidth={size * 0.028} />
        ) : (
          <g key={index} stroke="currentColor" strokeWidth={size * 0.032}
             strokeLinecap="round" opacity="0.55">
            <line x1={x - size * 0.05} y1={padTop - size * 0.19}
                  x2={x + size * 0.05} y2={padTop - size * 0.09} />
            <line x1={x - size * 0.05} y1={padTop - size * 0.09}
                  x2={x + size * 0.05} y2={padTop - size * 0.19} />
          </g>
        );
      })}

      {/* the nut, thick only when the shape is actually at the top */}
      <line x1={padX} y1={padTop} x2={padX + gridW} y2={padTop}
            stroke="currentColor" strokeWidth={open ? size * 0.075 : size * 0.028} />

      {Array.from({ length: frets }, (_, i) => (
        <line key={`f${i}`} x1={padX} y1={padTop + (i + 1) * fretGap}
              x2={padX + gridW} y2={padTop + (i + 1) * fretGap}
              stroke="currentColor" strokeWidth={size * 0.022} opacity="0.5" />
      ))}
      {Array.from({ length: strings }, (_, i) => (
        <line key={`s${i}`} x1={padX + i * stringGap} y1={padTop}
              x2={padX + i * stringGap} y2={padTop + gridH}
              stroke="currentColor" strokeWidth={size * 0.022} opacity="0.5" />
      ))}

      {barreFret && barreFrom >= 0 && (
        <line
          x1={padX + barreFrom * stringGap}
          y1={padTop + fretGap * 0.5}
          x2={padX + (strings - 1) * stringGap}
          y2={padTop + fretGap * 0.5}
          stroke="currentColor" strokeWidth={size * 0.13} strokeLinecap="round"
        />
      )}

      {dots.map(({ fret, index }) => (
        <circle
          key={`d${index}`}
          cx={padX + index * stringGap}
          cy={padTop + (at(fret) - 0.5) * fretGap}
          r={size * 0.072}
          fill="currentColor"
        />
      ))}

      {!open && (
        <text x={padX - size * 0.06} y={padTop + fretGap * 0.72}
              textAnchor="end" fontSize={size * 0.17} fill="currentColor" opacity="0.7">
          {from}
        </text>
      )}
    </svg>
  );
}
